/**
 * Platform Module API Client
 *
 * Handles platform-level operations:
 * - Authentication (login, logout, password change)
 * - User management (profile, sessions)
 * - System configuration
 *
 * Module: platform
 * Backend: /api/v1/auth, /api/v1/users
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { attachCsrfInterceptor } from '@/modules/shared/csrfInterceptor';
import type { LoginCredentials, LoginResponse, MeResponse, User } from '@/types/auth';

// localStorage keys for bearer token storage (spec 3e-02 W3)
const TOKEN_KEY = 'qiyas_access_token';
const REFRESH_KEY = 'qiyas_refresh_token';

/** Derive a primary UserRole string from the auth-service roles array. */
function deriveRole(roles: string[]): string {
  if (!roles || roles.length === 0) return 'viewer';
  // Prefer known privileged roles in priority order
  const priority = ['admin', 'portfolio_manager', 'analyst', 'static_data', 'viewer'];
  const found = priority.find(r => roles.includes(r));
  return found ?? roles[0];
}

// Map the raw /auth/me payload → app User shape. Shared by login() and
// getCurrentUser() so session-restore-on-refresh derives `role` identically to
// fresh login. Without this, ProtectedRoute sees user.role === undefined and
// denies every route after a page refresh ("role: none").
function mapMeToUser(me: MeResponse): User {
  const fullName = [me.first_name, me.last_name].filter(Boolean).join(' ');
  return {
    id: me.id,
    username: me.username,
    email: me.email,
    role: deriveRole(me.roles) as any,
    roles: me.roles,
    first_name: me.first_name,
    last_name: me.last_name,
    firstName: me.first_name,
    lastName: me.last_name,
    full_name: fullName || me.username,
    tenant_id: me.tenant_id,
    is_active: me.is_active,
  };
}

class PlatformClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000,
      withCredentials: true,
    });

    // Request interceptor: Attach bearer token + correlation ID
    attachCsrfInterceptor(this.client);

    this.client.interceptors.request.use(
      (config) => {
        // Add correlation ID for request tracing
        const correlationId = crypto.randomUUID();
        config.headers['X-Correlation-ID'] = correlationId;

        // Attach bearer token to all requests except the login endpoint itself
        const url = config.url || '';
        if (!url.includes('/auth/login')) {
          const token = localStorage.getItem(TOKEN_KEY);
          if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
          }
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: Handle common errors
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          const currentPath = window.location.pathname;
          const requestUrl = error.config?.url || '';
          // Only skip redirect for the login call itself — 401 on /auth/me, /auth/refresh, etc.
          // should still clear tokens and redirect (token expired/invalid path).
          const isLoginCall = requestUrl.includes('/auth/login');
          if (!currentPath.startsWith('/login') && !currentPath.startsWith('/auth') && !isLoginCall) {
            console.warn(`Session expired (${status}) — redirecting to login`);
            window.location.href = '/auth';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // ==================== Authentication ====================

  /**
   * User login
   * POST /auth/login
   */
  async login(credentials: LoginCredentials): Promise<User> {
    // Step 1: POST /auth/login — returns {access_token, refresh_token, token_type, expires_in}
    // Auth-service expects JSON body; tenant_id must be a UUID (string 'default' → 401).
    // Login tenant defaults to the System platform tenant. A non-System tenant MUST be an
    // EXPLICIT per-login choice (credentials.tenant_id — e.g. a login-form tenant selector).
    // DO NOT fall back to the stored session tenant (qiyas_tenant_id/tenant_id): that made
    // login sticky to the last-logged-in user's tenant, so after a buy-side/treasury login
    // every subsequent login (incl. admin/System) was routed to that tenant and 401'd
    // ("Invalid username"). Bug fixed 2026-06-22. login_tenant_id (if a selector sets it) is
    // honored as a deliberate override; the session-tenant fallback is removed.
    const SYSTEM_TENANT = '6b597961-7300-4000-8000-000000000001';
    const loginTenant =
      (credentials as { tenant_id?: string }).tenant_id || SYSTEM_TENANT;
    const loginResponse = await this.client.post<LoginResponse>('/auth/login', {
      username: credentials.username,
      password: credentials.password,
      tenant_id: loginTenant,
    });

    const { access_token, refresh_token } = loginResponse.data;

    // Store tokens in localStorage so the request interceptor can attach them
    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(REFRESH_KEY, refresh_token);
    // Mirror under the un-prefixed keys the chief orchestrator console
    // components read (@chief services use 'access_token'/'tenant_id')
    localStorage.setItem('access_token', access_token);

    // Step 2: GET /auth/me — returns {id, username, email, first_name, last_name, is_active, tenant_id, roles[]}
    // Bearer header is now injected automatically by the request interceptor.
    const meResponse = await this.client.get<MeResponse>('/auth/me');
    const me = meResponse.data;
    if (me.tenant_id) {
      localStorage.setItem('qiyas_tenant_id', me.tenant_id);
      localStorage.setItem('tenant_id', me.tenant_id);
    }

    return mapMeToUser(me);
  }

  /**
   * User logout
   * POST /auth/logout
   */
  async logout(): Promise<void> {
    try {
      await this.client.post('/auth/logout');
    } finally {
      // Always clear stored tokens on logout (bearer flow)
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      localStorage.removeItem('qiyas_tenant_id');
      localStorage.removeItem('access_token');
      localStorage.removeItem('tenant_id');
    }
  }

  /**
   * Change password
   * POST /auth/change-password
   */
  async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    await this.client.post('/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    });
  }

  // ==================== User Management ====================

  /**
   * Get current user profile
   * GET /auth/me
   */
  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<MeResponse>('/auth/me');
    const me = response.data;
    if (me.tenant_id) {
      localStorage.setItem('qiyas_tenant_id', me.tenant_id);
      localStorage.setItem('tenant_id', me.tenant_id);
    }
    return mapMeToUser(me);
  }

  /**
   * Update user profile
   * PUT /auth/me
   */
  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await this.client.put<User>('/auth/me', data);
    return response.data;
  }

  // ==================== Session Management ====================

  /**
   * Get active user sessions
   * GET /auth/sessions
   */
  async getSessions(): Promise<any[]> {
    const response = await this.client.get<any[]>('/auth/sessions');
    return response.data;
  }

  /**
   * Revoke a session
   * DELETE /auth/sessions/{sessionId}
   */
  async revokeSession(sessionId: string): Promise<void> {
    await this.client.delete(`/auth/sessions/${sessionId}`);
  }

  // ==================== Admin User Management ====================

  /**
   * Create a new user (admin)
   * POST /admin/users
   */
  async createUser(data: any): Promise<any> {
    const response = await this.client.post('/admin/users', data);
    return response.data;
  }

  /**
   * Update a user (admin)
   * PUT /admin/users/{id}
   */
  async updateUser(id: number, data: any): Promise<any> {
    const response = await this.client.put(`/admin/users/${id}`, data);
    return response.data;
  }

  /**
   * Delete a user (admin)
   * DELETE /admin/users/{id}
   */
  async deleteUser(id: number): Promise<void> {
    await this.client.delete(`/admin/users/${id}`);
  }

  // ==================== Admin Group Management ====================

  /**
   * Create a new user group (admin)
   * POST /admin/user-groups
   */
  async createUserGroup(data: any): Promise<any> {
    // identity-service UserGroupCreate types is_active as a string ("true"/"false")
    const payload = { ...data, is_active: String(data?.is_active ?? true) };
    const response = await this.client.post('/admin/user-groups', payload);
    return response.data;
  }

  /**
   * Update a user group (admin)
   * PUT /admin/user-groups/{id}
   */
  async updateUserGroup(id: number | string, data: any): Promise<any> {
    const payload = data?.is_active === undefined ? data : { ...data, is_active: String(data.is_active) };
    const response = await this.client.put(`/admin/user-groups/${id}`, payload);
    return response.data;
  }

  /**
   * Delete a user group (admin)
   * DELETE /admin/user-groups/{id}
   */
  async deleteUserGroup(id: number): Promise<void> {
    await this.client.delete(`/admin/user-groups/${id}`);
  }

  // ==================== Report Templates ====================

  /**
   * Create a new report template
   * POST /report-templates
   */
  async createReportTemplate(data: any): Promise<any> {
    const response = await this.client.post('/report-templates/', data);
    return response.data;
  }

  /**
   * Update a report template
   * PUT /report-templates/{id}
   */
  async updateReportTemplate(id: number, data: any): Promise<any> {
    const response = await this.client.put(`/report-templates/${id}/`, data);
    return response.data;
  }

  /**
   * Delete a report template
   * DELETE /report-templates/{id}
   */
  async deleteReportTemplate(id: number): Promise<void> {
    await this.client.delete(`/report-templates/${id}`);
  }
}

// Singleton instance
export const platformClient = new PlatformClient();
export default platformClient;
